import { useEffect, useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { Users, User, MessageSquare } from 'lucide-react';
import Modal from './Modal';
import { CREATE_CLIENT } from '../graphql/admin/mutations/client';
import { CREATE_BOOKING } from '../graphql/mutations/booking';
import type { PackageDetails } from '../types/package';
import type { Formula } from '../types/package';

const TYPES_CHAMBRE = [
  { value: 'single', label: 'Single' },
  { value: 'double', label: 'Double' },
  { value: 'triple', label: 'Triple' },
  { value: 'quadruple', label: 'Quadruple' },
  { value: 'quintuple', label: 'Quintuple' },
];

type Personne = { type: 'adulte' | 'enfant'; nom: string; prenom: string; chambre: number };
type Bebe = { nom: string; prenom: string; chambre: number };
type Chambre = { type: string };

type FormState = {
  nbrAdulte: number;
  nbrEnfant: number;
  nbrBebe: number;
  nbrChambre: number;
  telephone: string;
  mail: string;
  formuleId: number;
  personnes: Personne[];
  bebes: Bebe[];
  chambres: Chambre[];
  paiement: string;
  montantAvance: string;
  montantPaye: string;
  statutDisponibilite: string;
  typeReservation: string;
  specialRequests: string;
};

type Theme = { primaryColor: string; secondaryColor: string };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  pkg: PackageDetails;
  selectedFormula: Formula | null;
  theme: Theme;
};

function buildInitialForm(formulaId: number): FormState {
  return {
    nbrAdulte: 1,
    nbrEnfant: 0,
    nbrBebe: 0,
    nbrChambre: 1,
    telephone: '',
    mail: '',
    formuleId: formulaId,
    personnes: [{ type: 'adulte', nom: '', prenom: '', chambre: 1 }],
    bebes: [],
    chambres: [{ type: 'double' }],
    paiement: 'non_paye',
    montantAvance: '',
    montantPaye: '',
    statutDisponibilite: 'attente',
    typeReservation: 'enligne',
    specialRequests: '',
  };
}

export default function ClientBookingFormModal({
  isOpen,
  onClose,
  pkg,
  selectedFormula,
  theme,
}: Props) {
  const [form, setForm] = useState<FormState | null>(null);
  const [done, setDone] = useState(false);
  const [submittedBookingId, setSubmittedBookingId] = useState<number | null>(null);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  const [createClient, { loading: loadingClient }] = useMutation(CREATE_CLIENT);
  const [createBooking, { loading: loadingBooking }] = useMutation(CREATE_BOOKING);

  const agencyId = pkg.agency?.id;

  useEffect(() => {
    if (isOpen && selectedFormula?.id) {
      setForm(buildInitialForm(selectedFormula.id));
      setDone(false);
      setSubmittedBookingId(null);
      setSubmitErr(null);
    }
  }, [isOpen, selectedFormula?.id]);

  const updatePersonnesEtBebesEtChambres = useCallback(
    (adulteCount: number, enfantCount: number, bebeCount: number, chambreCount: number) => {
      setForm((f) => {
        if (!f) return f;
        const total = adulteCount + enfantCount;
        let personnes = [...f.personnes];
        if (personnes.length < total) {
          for (let i = personnes.length; i < total; i++) {
            personnes.push({
              type: i < adulteCount ? 'adulte' : 'enfant',
              nom: '',
              prenom: '',
              chambre: 1,
            });
          }
        } else if (personnes.length > total) {
          personnes = personnes.slice(0, total);
        }
        personnes = personnes.map((p, idx) => ({
          ...p,
          type: idx < adulteCount ? 'adulte' : 'enfant',
          chambre: p.chambre > chambreCount ? 1 : p.chambre || 1,
        }));
        let bebes = [...(f.bebes || [])];
        if (bebes.length < bebeCount) {
          for (let i = bebes.length; i < bebeCount; i++) {
            bebes.push({ nom: '', prenom: '', chambre: 1 });
          }
        } else if (bebes.length > bebeCount) {
          bebes = bebes.slice(0, bebeCount);
        }
        bebes = bebes.map((b) => ({
          ...b,
          chambre: b.chambre > chambreCount ? 1 : b.chambre || 1,
        }));
        let chambres = [...f.chambres];
        if (chambres.length < chambreCount) {
          for (let i = chambres.length; i < chambreCount; i++) {
            chambres.push({ type: 'double' });
          }
        } else if (chambres.length > chambreCount) {
          chambres = chambres.slice(0, chambreCount);
        }
        return {
          ...f,
          personnes,
          nbrAdulte: adulteCount,
          nbrEnfant: enfantCount,
          nbrBebe: bebeCount,
          bebes,
          nbrChambre: chambreCount,
          chambres,
        };
      });
    },
    []
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'nbrChambre') {
      updatePersonnesEtBebesEtChambres(
        form!.nbrAdulte,
        form!.nbrEnfant,
        form!.nbrBebe,
        Math.max(1, parseInt(value, 10) || 1)
      );
    } else {
      setForm((f) => (f ? { ...f, [name]: value } : f));
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (!form) return;
    if (name === 'nbrAdulte' || name === 'nbrEnfant' || name === 'nbrBebe') {
      updatePersonnesEtBebesEtChambres(
        name === 'nbrAdulte' ? Math.max(1, parseInt(value, 10) || 1) : form.nbrAdulte,
        name === 'nbrEnfant' ? Math.max(0, parseInt(value, 10) || 0) : form.nbrEnfant,
        name === 'nbrBebe' ? Math.max(0, parseInt(value, 10) || 0) : form.nbrBebe,
        form.nbrChambre
      );
    }
  };

  const handlePersonneChange = (idx: number, field: keyof Personne, value: string | number) => {
    setForm((f) => {
      if (!f) return f;
      const personnes = [...f.personnes];
      personnes[idx] = { ...personnes[idx], [field]: value };
      return { ...f, personnes };
    });
  };

  const handleBebeChange = (idx: number, field: keyof Bebe, value: string | number) => {
    setForm((f) => {
      if (!f) return f;
      const bebes = [...(f.bebes || [])];
      bebes[idx] = { ...bebes[idx], [field]: value };
      return { ...f, bebes };
    });
  };

  const handleChambreTypeChange = (idx: number, value: string) => {
    setForm((f) => {
      if (!f) return f;
      const chambres = [...f.chambres];
      chambres[idx] = { ...chambres[idx], type: value };
      return { ...f, chambres };
    });
  };

  const calculateTotalPrice = (): number => {
    if (!pkg || !form) return 0;
    const fId = Number(form.formuleId);
    const selectedF = pkg.formulas?.find((x) => x.id === fId);
    if (!selectedF) return 0;
    if (selectedF.priceType === 'PER_PERSON' && selectedF.perPersonPrice) {
      return selectedF.perPersonPrice * (form.nbrAdulte + form.nbrEnfant);
    }
    if (selectedF.priceType === 'PER_ROOM' && selectedF.roomPrices) {
      let total = 0;
      form.chambres.forEach((ch) => {
        const key = ch.type as keyof typeof selectedF.roomPrices;
        total += selectedF.roomPrices?.[key] || 0;
      });
      return total;
    }
    return 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitErr(null);
    if (!form || !agencyId || !selectedFormula?.id) {
      setSubmitErr('Données incomplètes.');
      return;
    }
    const mainPerson = form.personnes[0];
    if (!mainPerson?.nom?.trim() || !mainPerson?.prenom?.trim()) {
      setSubmitErr('Indiquez le nom et le prénom du voyageur principal.');
      return;
    }
    if (!form.mail?.trim() || !form.telephone?.trim()) {
      setSubmitErr('Email et téléphone sont obligatoires.');
      return;
    }

    try {
      const { data: clientData } = await createClient({
        variables: {
          input: {
            firstName: mainPerson.nom,
            lastName: mainPerson.prenom,
            email: form.mail.trim(),
            phone: form.telephone.trim(),
            address: '',
            agencyId,
          },
        },
      });
      const clientId = clientData?.createClient?.id;
      if (!clientId) {
        setSubmitErr('Impossible d’enregistrer vos coordonnées. Réessayez plus tard.');
        return;
      }

      const totalPrice = calculateTotalPrice();
      const {
        formuleId: fid,
        specialRequests: sr,
        montantAvance: maStr,
        montantPaye: mpStr,
        ...bookingRest
      } = form;
      const input = {
        ...bookingRest,
        packageId: pkg.id,
        formulaId: Number(fid),
        travelerCount: form.nbrAdulte + form.nbrEnfant + form.nbrBebe,
        totalPrice,
        contactInfo: {
          nom: mainPerson.nom,
          prenom: mainPerson.prenom,
          telephone: form.telephone,
          mail: form.mail,
        },
        clientId,
        nom: mainPerson.nom,
        prenom: mainPerson.prenom,
        montantAvance: maStr && maStr !== '' ? Number(maStr) : null,
        montantPaye: mpStr && mpStr !== '' ? Number(mpStr) : null,
        specialRequests: sr?.trim() || null,
        status: 'PENDING',
        statutReservation: 'en attente',
      };

      const { data: bookingRes } = await createBooking({ variables: { input } });
      const newId = bookingRes?.createBooking?.id;
      if (typeof newId === 'number') setSubmittedBookingId(newId);
      setDone(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de l’envoi.';
      setSubmitErr(msg);
    }
  };

  const loading = loadingClient || loadingBooking;

  if (!selectedFormula?.id || !form) {
    return null;
  }

  const totalPrice = calculateTotalPrice();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      wide
      title="Demande de réservation"
    >
      <div className="space-y-1 pb-2 text-sm text-gray-600">
        <p className="font-medium text-gray-900">{pkg.title}</p>
        <p>
          Formule : <span className="text-gray-800">{selectedFormula.name}</span>
        </p>
        <p className="text-xs leading-relaxed text-gray-500">
          Renseignez les informations ci-dessous. L’agence <strong>{pkg.agency.name}</strong> traitera
          votre demande et vous recontactera.
        </p>
      </div>

      {done ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-900">
          <p className="font-medium">Demande envoyée</p>
          <p className="mt-2 text-sm">
            Merci ! Votre demande a bien été transmise à l’agence. Vous serez contacté(e) prochainement.
          </p>
          {submittedBookingId != null && (
            <p className="mt-3 text-sm">
              <span className="font-medium">Votre référence :</span>{' '}
              <span className="font-mono font-semibold">#{submittedBookingId}</span>
            </p>
          )}
          <p className="mt-2 text-xs leading-relaxed text-green-900/90">
            Pour suivre ou annuler votre demande, rendez-vous dans la section{' '}
            <a href="#ma-reservation" className="underline font-medium">
              Ma réservation
            </a>{' '}
            sur cette page (même email que ci-dessus).
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 w-full rounded-lg py-2.5 text-sm font-medium text-white"
            style={{ backgroundColor: theme.primaryColor }}
          >
            Fermer
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          <section className="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
            <div className="mb-3 flex items-center gap-2">
              <Users className="h-5 w-5 shrink-0 text-gray-700" />
              <h3 className="font-semibold text-gray-900">Voyageurs & chambres</h3>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Adultes</label>
                <input
                  type="number"
                  name="nbrAdulte"
                  min={1}
                  value={form.nbrAdulte}
                  onChange={handleNumberChange}
                  className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Enfants</label>
                <input
                  type="number"
                  name="nbrEnfant"
                  min={0}
                  value={form.nbrEnfant}
                  onChange={handleNumberChange}
                  className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Bébés</label>
                <input
                  type="number"
                  name="nbrBebe"
                  min={0}
                  value={form.nbrBebe}
                  onChange={handleNumberChange}
                  className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Chambres</label>
                <input
                  type="number"
                  name="nbrChambre"
                  min={1}
                  value={form.nbrChambre}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
                  required
                />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {form.chambres.map((ch, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Ch. {idx + 1}</span>
                  <select
                    value={ch.type}
                    onChange={(e) => handleChambreTypeChange(idx, e.target.value)}
                    className="rounded-lg border border-gray-300 px-2 py-1"
                  >
                    {TYPES_CHAMBRE.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-3">
              {form.personnes.map((p, idx) => (
                <div key={idx} className="rounded-lg border border-gray-200 bg-white p-3">
                  <p className="mb-2 text-xs font-medium text-gray-500">
                    {p.type === 'adulte' ? `Adulte ${idx + 1}` : `Enfant ${idx + 1 - form.nbrAdulte}`}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <input
                      type="text"
                      placeholder="Nom"
                      value={p.nom}
                      onChange={(e) => handlePersonneChange(idx, 'nom', e.target.value)}
                      className="min-w-[120px] flex-1 rounded-lg border border-gray-300 px-2 py-2 text-sm"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Prénom"
                      value={p.prenom}
                      onChange={(e) => handlePersonneChange(idx, 'prenom', e.target.value)}
                      className="min-w-[120px] flex-1 rounded-lg border border-gray-300 px-2 py-2 text-sm"
                      required
                    />
                    <select
                      value={p.chambre}
                      onChange={(e) =>
                        handlePersonneChange(idx, 'chambre', parseInt(e.target.value, 10))
                      }
                      className="rounded-lg border border-gray-300 px-2 py-2 text-sm"
                    >
                      {form.chambres.map((_, cidx) => (
                        <option key={cidx} value={cidx + 1}>
                          Chambre {cidx + 1}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
            {form.bebes.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-medium text-gray-600">Bébés</p>
                {form.bebes.map((b, idx) => (
                  <div key={idx} className="flex flex-wrap gap-2 rounded border border-gray-100 p-2">
                    <input
                      type="text"
                      placeholder="Nom"
                      value={b.nom}
                      onChange={(e) => handleBebeChange(idx, 'nom', e.target.value)}
                      className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Prénom"
                      value={b.prenom}
                      onChange={(e) => handleBebeChange(idx, 'prenom', e.target.value)}
                      className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                      required
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-gray-100 p-4">
            <div className="mb-3 flex items-center gap-2">
              <User className="h-5 w-5 text-gray-700" />
              <h3 className="font-semibold text-gray-900">Vos coordonnées</h3>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Téléphone</label>
                <input
                  type="tel"
                  name="telephone"
                  value={form.telephone}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  required
                  autoComplete="tel"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="mail"
                  value={form.mail}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  required
                  autoComplete="email"
                />
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-gray-100 p-4">
            <div className="mb-2 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-gray-700" />
              <h3 className="font-semibold text-gray-900">Message (optionnel)</h3>
            </div>
            <textarea
              name="specialRequests"
              value={form.specialRequests}
              onChange={handleChange}
              rows={3}
              placeholder="Demandes particulières, régime alimentaire, flexibilité des dates…"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </section>

          <div className="flex items-center justify-between border-t border-gray-200 pt-4">
            <span className="text-sm text-gray-600">Total estimé</span>
            <span className="text-lg font-bold tabular-nums" style={{ color: theme.primaryColor }}>
              {totalPrice.toLocaleString('fr-DZ')} DA
            </span>
          </div>

          {submitErr && <p className="text-center text-sm text-red-600">{submitErr}</p>}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: theme.primaryColor }}
            >
              {loading ? 'Envoi…' : 'Envoyer ma demande'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
